import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useScrollPosition } from '../../utils/scrollAnimations';
import useAuthRole from '../../hooks/useAuthRole';
import useSiteSettings from '../../hooks/useSiteSettings';
import { DEFAULT_NAVIGATION } from '../../constants/siteDefaults';
import './Header.css';

const Header = () => {
  const { user } = useAuthRole();
  const { settings: siteSettings } = useSiteSettings();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();
  const isScrolled = useScrollPosition(50);
  const navRef = useRef(null);

  const branding = siteSettings?.branding;
  const navigation = siteSettings?.navigation?.length
    ? siteSettings.navigation
    : DEFAULT_NAVIGATION.filter((item) => item.visible !== false);

  const isActive = (path) => location.pathname === path;
  const isHomeRoute = location.pathname === '/';
  const isWohdRoute = location.pathname.startsWith('/wohd');
  const isConferenceRoute =
    location.pathname.startsWith('/conference') ||
    (!isHomeRoute &&
      !isWohdRoute &&
      !location.pathname.startsWith('/admin') &&
      !location.pathname.startsWith('/member'));

  const isNavGroupActive = (item) => {
    if (item.nav_group === 'wohd') return isWohdRoute;
    if (item.nav_group === 'conference') return isConferenceRoute;
    return isActive(item.path);
  };

  const handleDropdownToggle = (dropdownId) => (e) => {
    e.preventDefault();
    setOpenDropdown(openDropdown === dropdownId ? null : dropdownId);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    if (openDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [openDropdown]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpenDropdown(null);
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  useEffect(() => {
    setOpenDropdown(null);
    setIsMenuOpen(false);
  }, [location.pathname]);

  const renderNavItem = (item) => {
    const children = (item.children || []).filter((child) => child.visible !== false);
    const hasChildren = children.length > 0;
    const dropdownId = item.id || item.label;

    if (!hasChildren) {
      return (
        <Link
          key={dropdownId}
          to={item.path}
          className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
          onClick={() => setIsMenuOpen(false)}
        >
          {item.label}
        </Link>
      );
    }

    return (
      <div
        key={dropdownId}
        className={`nav-group ${isNavGroupActive(item) ? 'active' : ''} ${
          openDropdown === dropdownId ? 'open' : ''
        }`}
      >
        <Link
          to={item.path}
          className="nav-link nav-link-parent"
          onClick={handleDropdownToggle(dropdownId)}
          aria-expanded={openDropdown === dropdownId}
          aria-controls={`nav-dropdown-${dropdownId}`}
        >
          {item.label}
        </Link>
        <div className="nav-dropdown" id={`nav-dropdown-${dropdownId}`}>
          {children.map((child) => (
            <Link
              key={child.id || child.path}
              to={child.path}
              className="nav-link"
              onClick={() => {
                setIsMenuOpen(false);
                setOpenDropdown(null);
              }}
            >
              {child.label}
            </Link>
          ))}
        </div>
      </div>
    );
  };

  return (
    <header className={`header ${isScrolled ? 'header-scrolled' : ''}`}>
      <div className="header-container">
        <Link to="/" className="logo">
          <img
            src={branding?.logo_url || '/images/tda-logo.png'}
            alt="Tanzania Dental Association"
            className="logo-image"
            loading="eager"
            decoding="async"
          />
          <div className="logo-text">
            <span className="logo-title">{branding?.site_title || 'Tanzania Dental Association'}</span>
            <span className="logo-tagline">
              {branding?.site_tagline || 'Conference & World Oral Health Week Portal'}
            </span>
          </div>
        </Link>

        <nav ref={navRef} className={`nav ${isMenuOpen ? 'nav-open' : ''}`} aria-label="Main navigation">
          {navigation.map((item) => renderNavItem(item))}

          {user ? (
            <Link
              to="/member/dashboard"
              className={`nav-link nav-link-member ${location.pathname.startsWith('/member') ? 'active' : ''}`}
              onClick={() => setIsMenuOpen(false)}
            >
              My Dashboard
            </Link>
          ) : (
            <>
              <Link
                to="/member/login"
                className={`nav-link nav-link-member ${isActive('/member/login') ? 'active' : ''}`}
                onClick={() => setIsMenuOpen(false)}
              >
                Member Login
              </Link>
              <Link
                to="/member/register"
                className={`nav-link nav-link-member ${isActive('/member/register') ? 'active' : ''}`}
                onClick={() => setIsMenuOpen(false)}
              >
                Join TDA
              </Link>
            </>
          )}
        </nav>

        <button
          className="menu-toggle"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
          aria-expanded={isMenuOpen}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </header>
  );
};

export default Header;
